import { ScrollParams } from 'react-virtualized';
import { ScreenOrientationType } from '../../../hooks/useScreenOrientation';
import {
  CfDataGridCardViewDefinition,
  CfDataGridData,
  CfDataGridForStoreDataProps,
  CfDataGridPagingMode,
  CfDataGridProps,
  CfDataGridStoreData,
} from '../CfDataGridProps';

export function getPagingMode<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): CfDataGridPagingMode | undefined {
  return 'data' in props ? props.data.pagingMode : props.storeData.pagingMode;
}

export function getFilterText<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): string | undefined {
  return 'data' in props ? props.data.filterText : props.storeData.filterText;
}

export function getProductKey<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): string | undefined {
  return 'data' in props ? props.data.productKey : props.storeData.productKey;
}

export function getFilterPlaceholderText<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): string | undefined {
  return 'data' in props ? props.data.filterPlaceholderText : props.storeData.filterPlaceholderText;
}

const getLoadingPageSize = (pageSize: number): number => (pageSize === 0 ? 25 : pageSize);

export function getItems<TItem>(data: CfDataGridData<TItem>): { item: TItem | undefined; isLoading: boolean }[] {
  return [...data.pages]
    .sort((a, b) => a.pageIndex - b.pageIndex)
    .flatMap((p) =>
      p.isLoading
        ? Array.from(Array(getLoadingPageSize(p.pageSize))).map(() => ({ item: undefined, isLoading: true }))
        : (p.items.map((item) => ({ item, isLoading: false })) as {
            item: TItem | undefined;
            isLoading: boolean;
          }[])
    );
}

export function getItemIdsFromDataGridProps<TItem, TAppState>(
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): string[] {
  let itemIds = [];
  if ('data' in dataGridProps) {
    itemIds = [...dataGridProps.data.pages]
      .sort((a, b) => a.pageIndex - b.pageIndex)
      .flatMap((p) => (p.isLoading ? [] : p.items.map((item) => dataGridProps.selectId(item))));
  } else {
    itemIds = [...dataGridProps.storeData.pages]
      .sort((a, b) => a.pageIndex - b.pageIndex)
      .flatMap((p) => (p.isLoading ? [] : p.itemIds));
  }
  return itemIds;
}

export function getItemIds<TItem, TAppState>(
  storeData: CfDataGridStoreData<TItem, TAppState>,
  customIdsAfter?: Set<string>
): { itemId: string | undefined; isLoading: boolean; after?: boolean }[] {
  return [...storeData.pages]
    .sort((a, b) => a.pageIndex - b.pageIndex)
    .flatMap((p) =>
      p.isLoading
        ? Array.from(Array(getLoadingPageSize(p.pageSize))).map(() => ({ itemId: undefined, isLoading: true }))
        : p.itemIds.flatMap((itemId) => {
            const result = [];

            result.push({ itemId, isLoading: false });

            let after = false;
            const afterIds = [...(customIdsAfter ?? [])];
            for (let i = 0; i < afterIds.length; i++) {
              if (itemId.toLowerCase().indexOf(afterIds[i].toLowerCase()) >= 0) {
                after = true;
                break;
              }
            }
            if (after) {
              result.push({ itemId, isLoading: false, after: true });
            }

            return result as {
              itemId: string | undefined;
              isLoading: boolean;
            }[];
          })
    );
}

export function getHasItems<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): boolean {
  return 'data' in props
    ? props.data.pages.filter((p) => p.items.length || p.isLoading).length > 0
    : props.storeData.pages.filter((p) => p.itemIds.length || p.isLoading).length > 0;
}

interface Page {
  pageIndex: number;
  pageSize: number;
  isLoading: boolean;
}

export function getLastPage<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): Page | undefined {
  const pages: Page[] =
    'data' in props
      ? props.data.pages.map((p) => ({ pageIndex: p.pageIndex, pageSize: p.pageSize, isLoading: p.isLoading }))
      : props.storeData.pages.map((p) => ({ pageIndex: p.pageIndex, pageSize: p.pageSize, isLoading: p.isLoading }));

  return [...pages].sort((a, b) => a.pageIndex - b.pageIndex).pop();
}

export function getTotalPageCount<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): number {
  return 'data' in props ? props.data.totalPageCount : props.storeData.totalPageCount;
}

export function getTotalItemCount<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): number {
  return 'data' in props ? props.data.totalItemCount : props.storeData.totalItemCount;
}

export function getIsLoading<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): boolean {
  const page = getLastPage(props);
  return page?.isLoading === true;
}

export function getLastPageNumberOfItems<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): number | undefined {
  if ('data' in props) {
    return [...props.data.pages].sort((a, b) => a.pageIndex - b.pageIndex).pop()?.items?.length;
  } else {
    return [...props.storeData.pages].sort((a, b) => a.pageIndex - b.pageIndex).pop()?.itemIds?.length;
  }
}

export const detectScrollBottom = (
  params: ScrollParams,
  lastPage: Page | undefined,
  pagingMode: CfDataGridPagingMode | undefined,
  totalPageCount: number,
  onNextPageRequested: (pageIndex: number) => void
): void => {
  if (lastPage === undefined) return;
  if (
    pagingMode === 'load-more-scroll' &&
    lastPage.pageIndex + 1 < totalPageCount &&
    lastPage.isLoading === false &&
    params.scrollHeight - params.scrollTop <= params.clientHeight + 300
  ) {
    onNextPageRequested((lastPage.pageIndex ?? 0) + 1);
  }
};

export function getColumnCount<TItem>(
  cardViewDefinition: CfDataGridCardViewDefinition<TItem>,
  orientation?: ScreenOrientationType
): number {
  if (orientation === undefined || cardViewDefinition.columnCountLandscape === undefined)
    return cardViewDefinition.columnCount;

  return orientation === ScreenOrientationType.Portrait
    ? cardViewDefinition.columnCount
    : cardViewDefinition.columnCountLandscape;
}
